import { z } from 'zod'
import type { CalculatorDefinition } from '@/types/calculator'
import { positiveMoney } from '@/calculators/shared/schema'
import { calculateTransferCost } from './calculation'
import { content, faqs } from './content'
import type { TransferCostInputs, TransferCostResult } from './types'

const schema = z.object({
  purchasePrice: positiveMoney('Enter a purchase price').refine((v) => v > 0, {
    message: 'Purchase price must be greater than 0',
  }),
  financed: z.boolean(),
  loanAmount: positiveMoney(),
})

const defaults: TransferCostInputs = {
  purchasePrice: 1_500_000,
  financed: true,
  loanAmount: 1_350_000,
}

export const transferCostDefinition: CalculatorDefinition<TransferCostInputs, TransferCostResult> =
  {
    id: 'transfer-cost',
    slug: 'transfer-cost-calculator',
    category: 'home',
    title: 'Property Transfer Cost Calculator',
    shortTitle: 'Transfer Costs',
    description:
      'Estimate transfer duty, conveyancing fees and the total cash needed to buy property in South Africa.',
    inputs: [
      {
        name: 'purchasePrice',
        label: 'Purchase price',
        type: 'money',
        required: true,
        unit: 'R',
        placeholder: 'e.g. 1 500 000',
      },
      {
        name: 'financed',
        label: 'Bond / finance registered?',
        type: 'checkbox',
        defaultValue: true,
      },
      {
        name: 'loanAmount',
        label: 'Bond / loan amount',
        type: 'money',
        required: false,
        unit: 'R',
        placeholder: 'e.g. 1 350 000',
        hint: 'Used to estimate bond registration costs',
      },
    ],
    defaults,
    schema,
    calculate: calculateTransferCost,
    resultSections: [
      {
        id: 'totalCashRequired',
        label: 'Estimated total cash required',
        format: 'currency',
        value: (r: TransferCostResult) => r.totalCashRequired,
        highlighted: true,
      },
      {
        id: 'transferDuty',
        label: 'Transfer duty',
        format: 'currency',
        value: (r: TransferCostResult) => r.transferDuty,
      },
      {
        id: 'conveyancingFee',
        label: 'Conveyancing fee estimate',
        format: 'currency',
        value: (r: TransferCostResult) => r.conveyancingFee,
      },
      {
        id: 'bondRegistrationFee',
        label: 'Bond registration fee estimate',
        format: 'currency',
        value: (r: TransferCostResult) => r.bondRegistrationFee,
      },
      {
        id: 'deedsOfficeFee',
        label: 'Deeds office fee',
        format: 'currency',
        value: (r: TransferCostResult) => r.deedsOfficeFee,
      },
      {
        id: 'deedsSearchFee',
        label: 'Deeds search / FICA',
        format: 'currency',
        value: (r: TransferCostResult) => r.deedsSearchFee,
      },
      {
        id: 'postagePetties',
        label: 'Postage & petties',
        format: 'currency',
        value: (r: TransferCostResult) => r.postagePetties,
      },
    ],
    assumptions: [
      {
        name: 'SARS Transfer Duty Tables',
        url: 'https://www.sars.gov.za/tax-types/transfer-duty/',
        reviewedAt: '2026-09-18',
        effectiveFrom: '2026-04-01',
        effectiveTo: '2027-02-28',
        version: '2027',
        notes:
          'Transfer duty rates apply equally to natural persons, companies and trusts (SARS, since 23 February 2011). Conveyancing and bond registration fees are simplified estimates; obtain a quote from your attorney.',
      },
    ],
    related: ['bond-calculator', 'rental-yield-calculator'],
    seo: {
      title: 'Transfer Duty Calculator South Africa',
      description:
        'Estimate transfer duty, conveyancing and bond registration costs — the total cash needed for a South African property purchase.',
      keywords: ['transfer cost calculator', 'transfer duty', 'South Africa property costs'],
    },
    content,
    faqs,
  }
